import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { PrimaryButton } from './styled/Button';
import { Input, FormGroup, FormLabel, FormError } from './styled/Input';
import { DEPLOYMENT_CONFIG } from '../../deployment.config';
import { appRegistry } from '../apps';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--ui-canvas) 0%, #e1e8ed 100%);
  padding: var(--space-4);
`;

const LoginCard = styled.div`
  background-color: var(--ui-surface);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 119, 181, 0.1);
  width: 100%;
  max-width: 440px;
  overflow: hidden;
  border: 1px solid var(--ui-border);
  display: flex;
  flex-direction: column;
`;

const LoginHeader = styled.div`
  padding: var(--space-8) var(--space-8) var(--space-4) var(--space-8);
  text-align: center;
`;

const LoginContent = styled.div`
  padding: 0 var(--space-8) var(--space-8) var(--space-8);
`;

const LogoWrapper = styled.div`
  width: 64px;
  height: 64px;
  background-color: var(--ui-surface-alt);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-4) auto;
  font-size: 32px;
  border: 2px solid var(--brand-primary);
  box-shadow: 0 4px 12px var(--ui-shadow);
`;

const BrandName = styled.h2`
  color: var(--brand-primary);
  font-size: 24px;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.5px;

`;

const WelcomeTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: var(--space-4) 0 var(--space-1) 0;
`;

const WelcomeSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
`;

const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1.5px solid var(--ui-border);
  padding: 12px 16px;
  transition: all var(--transition-fast);
  font-size: 15px;

  &:focus {
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 4px rgba(0, 119, 181, 0.1);
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const VisibilityButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: var(--text-secondary);
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--ui-surface-hover);
    color: var(--brand-primary);
  }
`;

const SubmitButton = styled(PrimaryButton)`
  width: 100%;
  margin-top: var(--space-5);
  padding: 14px;
  font-size: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 119, 181, 0.2);
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 119, 181, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      if (result.success) {
        window.location.href = '/';
      } else {
        setError(result.error || 'Invalid credentials. Please try again.');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <LogoWrapper>☁️</LogoWrapper>
          <BrandName>{appRegistry[DEPLOYMENT_CONFIG.defaultApp]?.name || DEPLOYMENT_CONFIG.defaultApp}</BrandName>
          <WelcomeTitle>Welcome Back</WelcomeTitle>
          <WelcomeSubtitle>Sign in to your account</WelcomeSubtitle>
        </LoginHeader>

        <LoginContent>
          <form onSubmit={handleSubmit}>
            {error && (
              <FormError style={{ 
                marginBottom: 'var(--space-4)', 
                padding: '12px', 
                backgroundColor: 'var(--section-error-bg)', 
                borderRadius: '8px',
                border: '1px solid var(--section-error-border)',
                color: 'var(--text-error-dark)',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                {error}
              </FormError>
            )}

            <FormGroup>
              <FormLabel htmlFor="email">Email Address</FormLabel>
              <StyledInput
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="demo@example.com"
                autoComplete="email"
              />
            </FormGroup>

            <FormGroup style={{ marginTop: 'var(--space-3)' }}>
              <FormLabel htmlFor="password">Password</FormLabel>
              <PasswordWrapper>
                <StyledInput
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{ paddingRight: '50px' }}
                  autoComplete="current-password"
                />
                <VisibilityButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </VisibilityButton>
              </PasswordWrapper>
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </SubmitButton>
          </form>
        </LoginContent>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
